import { useState } from 'react';

import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { Typography } from '@design-system/components/typography/typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Menu, MenuItem } from '@mui/material';

import {
  ProgramOffer,
  ProgramOfferState,
  SubscriptionSublabel,
} from '../../model/programs.types';
import {
  AddToOfferAction,
  AddToOfferDirectAction,
  RemoveFromSubscriptionAction,
} from '../../view-model/programs.viewModel.types';

import * as styles from './OffersCell.css';

type OffersCellProps = {
  offerState: ProgramOfferState;
  addToOfferActions: Array<AddToOfferAction>;
  addToOfferDirectAction: AddToOfferDirectAction | null;
  removeFromSubscriptionAction: RemoveFromSubscriptionAction | null;
};

export const OffersCell = ({
  offerState,
  addToOfferActions,
  addToOfferDirectAction,
  removeFromSubscriptionAction,
}: OffersCellProps) => {
  if (offerState.type === 'subscription') {
    return (
      <SubscriptionChip
        sublabel={offerState.sublabel}
        removeAction={removeFromSubscriptionAction}
      />
    );
  }

  if (offerState.type === 'otp') {
    const [firstOffer, ...restOffers] = offerState.offers;

    if (!firstOffer) {
      return (
        <Typography variant="body" size="small" fontWeight="normal">
          -
        </Typography>
      );
    }

    return (
      <div className={styles.offersList} data-testid="offers-cell-otp">
        <div className={styles.offerRow}>
          <span
            className={
              firstOffer.isActive
                ? styles.statusDot.active
                : styles.statusDot.inactive
            }
          />
          <OfferLabel offer={firstOffer} />
        </div>
        {!restOffers.length ? null : <MoreOffersLabel offers={restOffers} />}
      </div>
    );
  }

  if (addToOfferDirectAction) {
    return <DirectAddToOfferTrigger action={addToOfferDirectAction} />;
  }

  return <AddToOfferTrigger actions={addToOfferActions} />;
};

const OfferLabel = ({ offer }: { offer: ProgramOffer }) => (
  <div className={styles.offerMeta}>
    <Typography
      variant="body"
      size="small"
      fontWeight="normal"
      className={styles.offerName}
    >
      {offer.name}
    </Typography>
    {offer.status === 'DRAFT' ? (
      <Typography
        variant="body"
        size="small"
        fontWeight="normal"
        className={styles.draftIndicator}
      >
        Draft
      </Typography>
    ) : null}
  </div>
);

const sublabelDisplayMap: Record<
  Exclude<SubscriptionSublabel, null>,
  string
> = {
  exclusive: 'Exclusive',
  platform: 'Platform',
  'exclusive-and-platform': 'Exclusive & Platform',
};

type SubscriptionChipProps = {
  sublabel: SubscriptionSublabel;
  removeAction: RemoveFromSubscriptionAction | null;
};

const SubscriptionChip = ({
  sublabel,
  removeAction,
}: SubscriptionChipProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const runAndClose = () => {
    closeMenu();
    removeAction?.onClick();
  };

  return (
    <div
      className={styles.subscriptionWrapper}
      data-testid="offers-cell-subscription"
    >
      <div className={styles.subscriptionLabel}>
        <Typography
          variant="body"
          size="small"
          fontWeight="normal"
          className={styles.subscriptionText}
        >
          Subscription
        </Typography>
        {sublabel ? (
          <Typography
            variant="body"
            size="small"
            fontWeight="normal"
            className={styles.subscriptionSublabel}
          >
            {sublabelDisplayMap[sublabel]}
          </Typography>
        ) : null}
      </div>
      {removeAction ? (
        <>
          <button
            type="button"
            aria-label="Remove program from subscription"
            data-testid="programs-remove-from-subscription-menu"
            className={
              isOpen ? styles.minusButton.open : styles.minusButton.closed
            }
            onClick={openMenu}
          >
            <FontAwesomeIcon
              icon={faMinus}
              className={
                isOpen
                  ? styles.minusButtonIcon.open
                  : styles.minusButtonIcon.closed
              }
            />
          </button>
          <Menu
            anchorEl={anchorEl}
            open={isOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { className: styles.menuPaper } }}
          >
            <MenuItem
              data-testid={removeAction.testId}
              onClick={runAndClose}
              className={styles.menuItem}
            >
              {removeAction.label}
            </MenuItem>
          </Menu>
        </>
      ) : null}
    </div>
  );
};

const MoreOffersLabel = ({ offers }: { offers: Array<ProgramOffer> }) => (
  <Tooltip
    arrow
    variant="dark"
    title={
      <div className={styles.moreOffersTooltip}>
        {offers.map((o) => (
          <div key={o.id} className={styles.moreOffersTooltipRow}>
            <span>{o.name}</span>
            {o.status === 'DRAFT' ? (
              <span className={styles.draftIndicator}>Draft</span>
            ) : null}
          </div>
        ))}
      </div>
    }
  >
    <Typography
      variant="body"
      size="small"
      fontWeight="normal"
      className={styles.moreOffers}
      data-testid="offers-cell-more"
    >
      +{offers.length} more
    </Typography>
  </Tooltip>
);

type DirectAddToOfferTriggerProps = {
  action: AddToOfferDirectAction;
};

const DirectAddToOfferTrigger = ({ action }: DirectAddToOfferTriggerProps) => (
  <button
    type="button"
    className={styles.addToOfferWrapper}
    data-testid={action.testId}
    onClick={action.onClick}
  >
    <Typography
      variant="body"
      size="small"
      fontWeight="normal"
      className={styles.addToOfferLabel}
    >
      Add to Offer
    </Typography>
    <span className={styles.plusButton.closed} aria-hidden="true">
      <FontAwesomeIcon icon={faPlus} className={styles.plusButtonIcon.closed} />
    </span>
  </button>
);

type AddToOfferTriggerProps = {
  actions: Array<AddToOfferAction>;
};

const AddToOfferTrigger = ({ actions }: AddToOfferTriggerProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  if (actions.length === 0) return null;

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const runAndClose = (action: AddToOfferAction) => () => {
    closeMenu();
    if (action.disabled) return;
    action.onClick();
  };

  return (
    <>
      <button
        type="button"
        className={styles.addToOfferWrapper}
        data-testid="programs-add-to-offer-menu"
        onClick={openMenu}
      >
        <Typography
          variant="body"
          size="small"
          fontWeight="normal"
          className={styles.addToOfferLabel}
        >
          Add to Offer
        </Typography>
        <span
          className={isOpen ? styles.plusButton.open : styles.plusButton.closed}
          aria-hidden="true"
        >
          <FontAwesomeIcon
            icon={faPlus}
            className={
              isOpen ? styles.plusButtonIcon.open : styles.plusButtonIcon.closed
            }
          />
        </span>
      </button>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { className: styles.menuPaper } }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            data-testid={action.testId}
            onClick={runAndClose(action)}
            disabled={action.disabled}
            title={action.disabledReason}
            className={styles.menuItem}
          >
            <span
              className={action.disabled ? styles.mutedMenuItemText : undefined}
            >
              {action.label}
            </span>
            {action.disabledReason ? (
              <Typography
                variant="body"
                size="small"
                fontWeight="normal"
                className={styles.mutedMenuItemText}
              >
                {action.disabledReason}
              </Typography>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
